import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  useTheme,
  IconButton,
  Typography,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import { useFormik } from "formik";
import { BrokerName } from "../../component/Schemas";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;
const EMSX_ROUTER_KEY = "7b16a3667dcc8252a857d4a322d4e143";

const BrokerDialog = ({ open, onClose, broker, onSave }) => {
  const theme = useTheme();

  const initialValues = {
    name: broker?.name || "",
    abbreviation: broker?.abbreviation || "",
    emsxName: broker?.routers?.[EMSX_ROUTER_KEY]?.name || broker?.emsxName || "",
    commission: broker?.commission || 0,
    brokerId: broker?.brokerId,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: BrokerName,
    enableReinitialize: true,
    onSubmit: (values) => {
      const formattedRouters = [
        {
          routerId: "7b16a3667dcc8252a857d4a322d4e143",
          name: values.abbreviation,
        },
      ];

      const formData = {
        ...values,
        routers: formattedRouters,
      };

      delete formData.emsxName;

      onSave(formData);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 4px 24px 0 rgba(16,30,54,0.10)",
          fontFamily,
          p: 0,
        },
      }}
    >
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32,
              }}
            >
              <BusinessIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  fontFamily,
                  lineHeight: 1.2,
                }}
              >
                {broker ? "Edit Broker" : "Add Broker"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily,
                  mt: 0.5,
                }}
              >
                {broker
                  ? "Update broker details below."
                  : "Enter broker details to add a new broker."}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
             <DialogContent sx={{ pt: 2, pb: 2, px: 4 }}>
         <form onSubmit={formik.handleSubmit}>
           <Stack spacing={2.5}>
             <TextField
               label="Name *"
               name="name"
               value={formik.values.name}
               onChange={formik.handleChange}
               onBlur={formik.handleBlur}
               fullWidth
               error={formik.touched.name && Boolean(formik.errors.name)}
               helperText={formik.touched.name && formik.errors.name}
               InputLabelProps={{
                 shrink: true,
                 sx: { fontWeight: 600, fontFamily },
               }}
               inputProps={{ style: { fontFamily } }}
               variant="outlined"
             />
             <TextField
               label="Abbreviation *"
               name="abbreviation"
               value={formik.values.abbreviation}
               onChange={formik.handleChange}
               disabled={broker}
               onBlur={formik.handleBlur}
               fullWidth
               error={formik.touched.abbreviation && Boolean(formik.errors.abbreviation)}
               helperText={formik.touched.abbreviation && formik.errors.abbreviation}
               InputLabelProps={{
                 shrink: true,
                 sx: { fontWeight: 600, fontFamily },
               }}
               inputProps={{ style: { fontFamily } }}
               variant="outlined"
             />
             <TextField
               label="EMSX Name"
               name="emsxName"
               value={formik.values.emsxName}
               onChange={formik.handleChange}
               onBlur={formik.handleBlur}
               fullWidth
               InputLabelProps={{
                 shrink: true,
                 sx: { fontWeight: 600, fontFamily },
               }}
               inputProps={{ style: { fontFamily } }}
               variant="outlined"
             />
             <TextField
               label="Default Commission"
               name="commission"
               type="number"
               step="0.01"
               value={formik.values.commission}
               onChange={formik.handleChange}
               onBlur={formik.handleBlur}
               fullWidth
               InputLabelProps={{
                 shrink: true,
                 sx: { fontWeight: 600, fontFamily },
               }}
               inputProps={{ style: { fontFamily } }}
               variant="outlined"
             />
           </Stack>
           
           <DialogActions sx={{ px: 0, pb: 0, pt: 2 }}>
             <Button
               type="submit"
               variant="contained"
               color="primary"
               sx={{
                 borderRadius: 1,
                 fontWeight: 600,
                 fontFamily,
                 textTransform: "none",
                 px: 3,
                 minWidth: 120,
               }}
             >
               {broker ? "Save" : "Add Broker"}
             </Button>
           </DialogActions>
         </form>
       </DialogContent>
    </Dialog>
  );
};

export default BrokerDialog;
